import {
  DerivedAvailabilityStatusInternal,
  checkForPartialBackOrder,
  contentStackApi,
  logObject,
  productApi,
  productDataTransformer,
  type GenericObject,
  type IProduct,
  type InstructionsInner,
  type ProductData,
} from '@commerce-product-webcomponents/sdk';
import {
  BundleComponent,
  ButtonContainer,
  FAQsComponent,
  ImageModal,
  PDPNavigator,
  ProductDetails,
  ProductGallery,
  ProductGalleryV2,
  ProductGalleryStyleType,
  ProductIngredients,
  PromiseComponent,
  ShortDescription,
  SourceTypeEnums,
  UsageComponent,
  Variant,
  checkIfProductIsNya,
  getButtonType,
  getFulfillmentType,
  includesDerivedAvailabilityStockStatus,
  isSopQueryParamAvailable,
  FeatureFlags,
  type FAQType,
  type IBundleState,
} from '@commerce-product-webcomponents/ui';
import { PurchaseTypes } from '@commerce-subscription-webcomponents/ui-composite';
import { scrollToElementId, useFeatureVisibility, useNextgenRouter, useSession } from '@nextgen-web-framework/all';
import { Logger, StorageService } from '@nextgen-web-framework/core';
import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { RecommendedProducts } from '../Molecules';
import {
  ProductSpecs,
  SubscriptionContainer,
  SubscriptionContainerIds,
  checkIfValidSubscriptionPlansAvailable,
} from '../Organisms';
import styles from './ProductContainer.module.scss';
import {
  calculateBundlePrice,
  getRespectivePrice,
  getUpdatedStateNavigationTabs,
  onBundleProductSelected,
} from './productContainerUtil';
import { ToastMessage } from './ToastMessage';
import { SectionIds } from './types/enums';
import type {
  BundleSlotEntriesType,
  BundleSlotType,
  DynamicBundleValuesType,
  ProductContainerProps,
} from './types/propsTypes';

const localStorageAPI = StorageService.getLocalStorage();
const loggerInstance = Logger.getInstance(logObject('ProductContainer'));

export const ProductContainer: FC<ProductContainerProps> = (props: ProductContainerProps): JSX.Element => {
  const {
    productData,
    disabled,
    entryObj,
    handleDataRefresh,
    handleSubscription,
    handleProductDataOrError,
    subscriptionPlans,
    dataFlow,
    size,
    promotionData,
    subscriptionPlanData,
    sharebarLocalStorageKey = '',
    isSharedDataLoaded = false,
    showSubText = true,
    dateSubText,
    showZeroAsFree = false,
  } = props;

  const [isCartDisabled, setIsCartDisabled] = useState<boolean>(disabled as boolean);
  const [dynamicBundleValues, setDynamicBundleValues] = useState<DynamicBundleValuesType>(() =>
    calculateBundlePrice(productData.bundle, productData.isBundle),
  );
  const [isBundleConditionMatched, setIsBundleConditionMatched] = useState<{ value: boolean }>({ value: false });
  const [bundleSlots, setBundleSlots] = useState<BundleSlotType[]>([]);
  const [isSubscription, setIsSubscription] = useState<boolean>(false);
  const [updatedBundleState, setUpdatedBundleState] = useState<IBundleState[]>([]);
  const [bundleStoredState, setBundleStoredState] = useState<IBundleState[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [galleryId, setGalleryId] = useState<string>('');
  const [galleryIdV2, setGalleryIdV2] = useState<number | string>(0);
  const [contentStackData, setContentStackData] = useState<GenericObject[]>([]);
  const [newBundleDerivedStatus, setNewBundleDerivedStatus] = useState<DerivedAvailabilityStatusInternal>(
    productData.derivedAvailabilityStatus as DerivedAvailabilityStatusInternal,
  );
  const [newFirstAvailableFutureDate, setNewFirstAvailableFutureDate] = useState<string>(
    productData.firstAvailableFutureDate || '',
  );
  const [updatedProductData, setUpdatedProductData] = useState<IProduct>(productData);
  const [isCachedDataLoaded, setIsCachedDataLoaded] = useState<boolean>(false);
  const [purchaseType, setPurchaseType] = useState(PurchaseTypes.ONETIME);

  const isSopPDPFeatureAvailable = useFeatureVisibility(FeatureFlags.sopPdpFeature);
  const isAprPDPFeatureAvailable = useFeatureVisibility(FeatureFlags.aprPdpFeature);
  const showPdpProductGalleryV2 = useFeatureVisibility(FeatureFlags.pdpProductGalleryV2);

  const {
    session: { warehouseId },
  } = useSession();

  const router = useNextgenRouter();
  const selectedLanguage = router.params?.locale;
  const sharecodeId = router.params?.sharecodeid;
  const isSop = isSopQueryParamAvailable(router.query?.get('sop') || '');

  const isValidSubscriptionPlanAvailable = checkIfValidSubscriptionPlansAvailable(
    subscriptionPlans,
    updatedProductData.productFlags?.restrictFromSubscription,
  );

  useEffect(() => {
    if (isSop && isValidSubscriptionPlanAvailable) {
      selectSubscribeAndSaveOption();
    }
  }, [isSop, isValidSubscriptionPlanAvailable]);

  useEffect(() => {
    if (bundleStoredState && bundleStoredState.length > 0) {
      const storedData = getStoredCachedData();
      const isBundleConditionSatisfied = storedData?.data
        ? JSON.parse(storedData.data)?.isBundleConditionMatched
        : null;
      setIsBundleConditionMatched(isBundleConditionSatisfied);
      handleBundleSelection(bundleStoredState, isBundleConditionSatisfied, isBundleConditionSatisfied?.value, 1, {
        value: true,
      });
    }
  }, [bundleStoredState]);

  const getStoredCachedData = () =>
    localStorageAPI.retrieve(sharebarLocalStorageKey || '') as unknown as { data: string };

  const loadBundleSharedData = () => {
    if (bundle) {
      const storedData = getStoredCachedData();
      const bundleStoredState = storedData?.data ? JSON.parse(storedData.data)?.[productData.productId] : null;
      if (bundleStoredState && bundleStoredState.length > 0) {
        setBundleStoredState(bundleStoredState);
      }
    }
  };

  const selectSubscribeAndSaveOption = () => {
    scrollToElementId(SubscriptionContainerIds.SUBSCRIPTION_CONTAINER);
    setPurchaseType(PurchaseTypes.SUBSCRIPTION);
    handleSubscribeButtonVisibilty(true);
  };

  const getProductData = (productId: string, warehouseId?: string) => {
    setIsCartDisabled(true);
    productApi
      .getProductByIdOrError({ productId, warehouseId, fulfillmentType: getFulfillmentType() })
      .then((prodData: ProductData | Response) => {
        if (!(prodData instanceof Response)) {
          const transformedProdData = productDataTransformer(prodData);
          const updatedProductData = { ...productData, ...transformedProdData };
          handleProductDataOrError?.(updatedProductData);
          setUpdatedProductData(updatedProductData);
          setIsCachedDataLoaded(true);
          setIsCartDisabled(false);
          getContentStackApi(updatedProductData);
        } else {
          handleProductDataOrError?.(prodData);
        }
      })
      .catch((e) => {
        loggerInstance.error('Error occured ', e);
        handleProductDataOrError?.(productData);
        setUpdatedProductData(productData);
        setIsCachedDataLoaded(false);
      });
  };

  const getContentStackApi = (data: IProduct) => {
    contentStackApi
      .getProductDetailsContentStackData({ productData: data })
      .then((contentStackResData) => {
        if (!(contentStackResData instanceof Response)) {
          setContentStackData(contentStackResData);
        }
      })
      .catch((error) => {
        loggerInstance.error('Unexpected error in getContentStackApi:', error);
      });
  };

  const {
    galleryList,
    brandName,
    productName,
    productId,
    productSize,
    productPrice,
    currency,
    shortDescription,
    faqs,
    variantData,
    defaultVariantItemId,
    ingredientsData,
    productDetail,
    bundle,
    isBundle,
    minPrice,
    maxPrice,
    instructions,
    pointValue,
    businessVolume,
    promiseDetails,
    productRetailPrice,
    minRetailPrice,
    maxRetailPrice,
  } = updatedProductData;

  useEffect(() => {
    getProductData(productData.productId, warehouseId);
  }, [selectedLanguage, warehouseId, productData.productId]);

  useLayoutEffect(() => {
    if (Object.keys(updatedProductData).length > 0) {
      setNewBundleDerivedStatus(checkForPartialBackOrder(updatedProductData));
      setNewFirstAvailableFutureDate(updatedProductData.firstAvailableFutureDate || '');
      setDynamicBundleValues(calculateBundlePrice(bundle, isBundle));
    }
  }, [updatedProductData]);

  useEffect(() => {
    if (isSharedDataLoaded && sharecodeId) {
      loadBundleSharedData();
    }
  }, [isSharedDataLoaded]);

  const getTabsCollection = getUpdatedStateNavigationTabs(updatedProductData);
  const onCloseModal = () => {
    setShowModal(false);
    setGalleryId('');
    setGalleryIdV2(0);
  };
  const onOpenModal = (galleryId: string | number) => {
    setShowModal(true);
    setGalleryId(galleryId.toString());
    setGalleryIdV2(Number(galleryId));
  };

  const handleSubscribeButtonVisibilty = (isPeriodicSubscription: boolean) => {
    setIsSubscription(isPeriodicSubscription);
    if (!isPeriodicSubscription) {
      setPurchaseType(PurchaseTypes.ONETIME);
    }
  };

  const handleBundleSelection = (
    bundleState: IBundleState[],
    isBundleConditionMatched: { value: boolean },
    enableAddToCart: boolean,
    minPoolQty: number,
    isBundleUpdated: { value: boolean },
    showToast = true,
  ) => {
    onBundleProductSelected(
      bundleState,
      isBundleConditionMatched,
      enableAddToCart,
      minPoolQty,
      isBundleUpdated,
      bundle,
      isBundle,
      setIsBundleConditionMatched,
      setShowToast,
      setDynamicBundleValues,
      dynamicBundleValues,
      setUpdatedBundleState,
      setBundleSlots,
      setIsCartDisabled,
      setNewBundleDerivedStatus,
      setNewFirstAvailableFutureDate,
      showToast,
    );
  };

  return (
    <>
      {showModal && (
        <ImageModal onCloseModal={onCloseModal}>
          {showPdpProductGalleryV2 ? (
            <ProductGalleryV2
              galleryList={galleryList}
              galleryId={galleryIdV2}
              sourceKey={SourceTypeEnums.MODAL_GALLERY}
              productGalleryStyleType={ProductGalleryStyleType.PRODUCT_GALLERY_MODAL}
            />
          ) : (
            <ProductGallery
              galleryList={galleryList}
              galleryId={galleryId}
              sourceKey={SourceTypeEnums.MODAL_GALLERY}
              productGalleryStyleType={ProductGalleryStyleType.PRODUCT_GALLERY_MODAL}
            />
          )}
        </ImageModal>
      )}
      {isBundle && showToast && (
        <ToastMessage
          isBundleConditionMatched={isBundleConditionMatched}
          currency={currency}
          finalProductPrice={dynamicBundleValues.productPrice}
        />
      )}
      <PDPNavigator tabsCollection={getTabsCollection} />
      <div className={styles['product-detail-section']} data-testid="product-detail-section">
        <section
          className={styles['primary-product-details']}
          id={SectionIds.OVERVIEW}
          data-section-scroll-id={SectionIds.OVERVIEW}
          data-testid={SectionIds.OVERVIEW}
        >
          {showPdpProductGalleryV2 ? (
            <ProductGalleryV2
              galleryList={galleryList}
              galleryId={galleryIdV2}
              openModal={onOpenModal}
              sourceKey={SourceTypeEnums.PRODUCT_DETAIL_GALLERY}
            />
          ) : (
            <ProductGallery
              galleryList={galleryList}
              galleryId={galleryId}
              openModal={onOpenModal}
              sourceKey={SourceTypeEnums.PRODUCT_DETAIL_GALLERY}
            />
          )}
          <div className={styles['product-spec-container']}>
            <ProductSpecs
              brandName={brandName}
              productName={productName}
              productId={productId}
              productSize={productSize}
              productPrice={productPrice}
              currency={currency}
              derivedAvailabilityStatus={newBundleDerivedStatus}
              firstAvailableFutureDate={newFirstAvailableFutureDate}
              showSubText={showSubText}
              dateSubText={dateSubText}
              hasVariant={!!variantData && Object.keys(variantData).length > 0}
              isBundle={isBundle}
              minPrice={Number(
                getRespectivePrice(
                  Number(minPrice),
                  bundle,
                  isBundleConditionMatched,
                  dynamicBundleValues.productPrice,
                ),
              )}
              maxPrice={Number(
                getRespectivePrice(
                  Number(maxPrice),
                  bundle,
                  isBundleConditionMatched,
                  dynamicBundleValues.productPrice,
                ),
              )}
              pointValue={getRespectivePrice(
                pointValue,
                bundle,
                isBundleConditionMatched,
                Number(dynamicBundleValues.pointValue),
              ).toString()}
              businessVolume={getRespectivePrice(
                businessVolume,
                bundle,
                isBundleConditionMatched,
                Number(dynamicBundleValues.buisnessVolume),
              ).toString()}
              promotionData={promotionData}
              productData={updatedProductData}
              subscriptionPlanData={subscriptionPlanData}
              bundleStateToBeStored={updatedBundleState}
              isCachedDataLoaded={isCachedDataLoaded}
              showZeroAsFree={showZeroAsFree}
              productRetailPrice={productRetailPrice}
              minRetailPrice={Number(
                getRespectivePrice(
                  Number(minRetailPrice),
                  bundle,
                  isBundleConditionMatched,
                  dynamicBundleValues.retailPrice || 0,
                ),
              )}
              maxRetailPrice={Number(
                getRespectivePrice(
                  Number(maxRetailPrice),
                  bundle,
                  isBundleConditionMatched,
                  dynamicBundleValues.retailPrice || 0,
                ),
              )}
              isBundleConditionMatched={isBundleConditionMatched}
            />
            {bundle && productData.isBundle && (
              <BundleComponent
                bundle={bundle}
                onBundleProductSelected={handleBundleSelection}
                bundleStoredState={sharecodeId ? bundleStoredState : []}
                showSubText={showSubText}
                dateSubText={dateSubText}
                showZeroAsFree={showZeroAsFree}
              />
            )}
            <Variant
              variantData={variantData}
              handleDataRefresh={handleDataRefresh}
              defaultVariantItemId={defaultVariantItemId}
              showSubText={showSubText}
              dateSubText={dateSubText}
              isCachedDataLoaded={isCachedDataLoaded}
            />
            <SubscriptionContainer
              subscriptionPlans={subscriptionPlans}
              showSubscribeButton={handleSubscribeButtonVisibilty}
              restrictFromSubscription={updatedProductData.productFlags?.restrictFromSubscription}
              purchaseType={purchaseType}
              productData={updatedProductData}
            />
            <ShortDescription shortDescription={shortDescription} />

            <ButtonContainer
              buttonType={getButtonType({
                isNotAvailable: includesDerivedAvailabilityStockStatus(newBundleDerivedStatus),
                isSubscription,
                isBundle,
                isNYA: checkIfProductIsNya(newBundleDerivedStatus),
                isAvailableSoon: !!updatedProductData.productFlags?.restrictFromAddToCart,
              })}
              derivedAvailabilityStatus={newBundleDerivedStatus}
              disabled={isCartDisabled}
              entryObj={entryObj}
              className={'sticky-bottom-bar'}
              bundleSlots={bundleSlots}
              handleSubscription={handleSubscription}
              dataFlow={dataFlow}
              size={size}
              productData={updatedProductData}
              firstAvailableFutureDate={newFirstAvailableFutureDate}
              isSubscriptionAvailable={isSopPDPFeatureAvailable ? isValidSubscriptionPlanAvailable : false}
              handleSubscriptionSelectionButtonClick={isSopPDPFeatureAvailable ? selectSubscribeAndSaveOption : void 0}
            />
          </div>
        </section>
        {promiseDetails && (
          <section
            id={SectionIds.PROMISES}
            data-section-scroll-id={SectionIds.PROMISES}
            data-testid={SectionIds.PROMISES}
            className={styles['product-properties-section']}
          >
            <PromiseComponent promiseDetails={promiseDetails} />
          </section>
        )}
        {(productDetail || contentStackData.length > 0) && (
          <section
            id={SectionIds.PRODUCT_DETAILS}
            data-section-scroll-id={SectionIds.PRODUCT_DETAILS}
            data-testid={SectionIds.PRODUCT_DETAILS}
          >
            <ProductDetails productDetail={productDetail} cmsData={contentStackData} />
          </section>
        )}
        {!!ingredientsData.ingredients && (
          <section
            id={SectionIds.INGREDIENTS}
            data-section-scroll-id={SectionIds.INGREDIENTS}
            data-testid={SectionIds.INGREDIENTS}
            className={styles['product-properties-section']}
          >
            <ProductIngredients ingredientsData={ingredientsData} />
          </section>
        )}
        {instructions && instructions.length > 0 && (
          <section
            id={SectionIds.HOW_TO_USE}
            data-section-scroll-id={SectionIds.HOW_TO_USE}
            data-testid={SectionIds.HOW_TO_USE}
            className={styles['product-properties-section']}
          >
            <UsageComponent usageDetails={instructions as InstructionsInner[]} />
          </section>
        )}
        {faqs.length > 0 && (
          <section
            className={styles['product-properties-section']}
            id={SectionIds.FAQS}
            data-section-scroll-id={SectionIds.FAQS}
            data-testid={SectionIds.FAQS}
          >
            <FAQsComponent faqs={faqs as FAQType[]} />
          </section>
        )}
        {isAprPDPFeatureAvailable && (
          <section className={styles['product-properties-section']}>
            <RecommendedProducts />
          </section>
        )}
      </div>
    </>
  );
};
export { SectionIds };
export type { BundleSlotEntriesType, BundleSlotType, ProductContainerProps };
